// FLOW-01 (task 5.3, D15/D16) — o canvas desenhado a partir do YAML: um nó por passo, as conexões
// na ordem em que o flow roda, e o layout de cima para baixo pelo dagre.
//
// Sai do TEXTO, e não do `flow_spec`: o `flow_spec` só existe quando o flow compila, e o canvas tem
// de acompanhar o código também com erro, a cada tecla. O id de cada nó é o caminho do passo como o
// compilador o escreve (`steps[buscar].then[0]`, `compilador.py`, `_caminho`), que é também o
// `caminho` dos eventos de depuração (D17): o estado ao vivo cai no nó sem tradução.
//
// FLOW-04 (task 4.1): o corpo de um laço desce como um ramo e fecha num marco "fim da volta"; a
// seta da próxima volta (`data.volta`) sobe dele até a porta lateral do laço e fica fora do dagre,
// que não vê ciclo. O que vem depois do laço sai da porta da esquerda dele (`depois`), mas o dagre
// o põe abaixo do fim da volta (`layoutDe`), para não ficar ao lado do corpo. A rota de erro
// (`on_error: {steps}`) desce pela saída `erro` e volta ao passo seguinte, como um ramo.
//
// Cada conexão sabe onde um passo novo entraria nela (`insercao`: a lista e a posição), que é o
// que o "+" e o soltar da biblioteca usam:
// - entre dois passos da mesma lista, na posição do de baixo;
// - do `if` para o primeiro do `then` (ou do `else`), no começo dessa lista;
// - do "falso" de um `if` sem `else` (e do "padrão" de um `switch` sem `default`), num `else`
//   (ou `default`) que nasce com o passo;
// - na junção dos ramos de um `if`, `switch` ou `branches` (do fim de cada ramo para o que vem
//   depois do bloco), DEPOIS do bloco, na lista de fora. Correção de 14/10: ela inseria no fim do
//   ramo, e quem a usava achando que punha o passo depois do `if` o via sumir ao apagar o `if`.
//   A conexão leva o bloco em `juncao`, e uma só delas (`maisDaJuncao`) desenha o "+ depois do
//   bloco". O "depois do laço" e o "falso"/"padrão" que criam a lista ficam com o lugar deles; a
//   rota de erro e o `else` do `validate` também, porque a saída do próprio passo já insere depois
//   dele. A junção que chega ao fim da volta de um laço (o bloco no fim do corpo) também insere
//   depois do bloco, no corpo (correção de 14/10, aprovada pelo Murilo).
//
// FLOW-11 (task 5.3, S11): as correntes soltas (`detached`) descem como grupos sem entrada
// (`data.solto`), e o passo desativado (`disabled: true`, e tudo dentro dele) vem esmaecido
// (`data.desativado`). Para os gestos do canvas (5.4), cada nó diz onde entra o passo ligado a cada
// saída dele (`data.insercoes`, o lugar da SPEC: logo depois dele, ou o começo da lista daquela
// saída) e quais saídas não têm conexão (`data.saidasLivres`, o "+" na ponta); cada conexão diz o
// que apagá-la faz no YAML (`data.apagar`, o argumento do `soltarTrecho`): o trecho de baixo vira
// corrente solta, a lista opcional sai inteira, e a junção, o laço e a lista que não fica vazia
// recusam.
//
// FLOW-03b (Task 3.5; PLAN P11, A2): o nó de topo diz o gatilho do flow, um só (`gatilhoDoFlow`): o
// tipo, o bloco dele, a frase da agenda e, na agenda e no webhook, se a versão publicada está ligada.
// O texto do resumo sai no nó, no idioma da tela, como as linhas do código.
import { graphlib, layout } from '@dagrejs/dagre';
import { isMap, isSeq } from 'yaml';
import { documentoLido } from './leituraYaml';
import { escapar, ID_VALIDO, opcional } from './documentoYaml';
import { formaDeFerramentaHttp } from './formaDaFerramenta';
import { tipoDoGatilho } from './tiposDeGatilho';

const LARGURA_DO_NO = 260;
const ALTURA_DO_NO = 76;
const GATILHO = 'gatilho';
const TERMINAIS = ['return', 'fail'];
// FLOW-04: os laços e o marco "fim da volta", onde o corpo termina e a seta da próxima volta nasce.
const LACOS = ['for_each', 'while'];
export const FIM_DA_VOLTA = 'fim';
// O marco tem a largura do nó, para a porta de cima cair na coluna dos quadrados (a 36px da
// esquerda), e a altura da pílula.
const DIMENSOES = {
  [FIM_DA_VOLTA]: { width: LARGURA_DO_NO, height: 28 },
};
const dimensoes = no =>
  DIMENSOES[no.type] || { width: LARGURA_DO_NO, height: ALTURA_DO_NO };
// FLOW-11 (task 5.12): o tamanho de cada nó, o mesmo do layout, para o canvas de um flow grande
// desenhar só o que está na vista desde o primeiro quadro.
export const dimensoesDoNo = dimensoes;
const METODOS_DE_LEITURA = ['GET', 'HEAD'];
const CHAVE_SIMPLES = /^[A-Za-z_][A-Za-z0-9_-]*$/;

const semChaves = valor =>
  String(valor ?? '')
    .replace(/^\s*\{\{\s*/, '')
    .replace(/\s*\}\}\s*$/, '');

const json = no => (no && typeof no.toJSON === 'function' ? no.toJSON() : no);

// O caminho da URL ("POST /leads"); a URL que é expressão aparece como está.
const caminhoDaUrl = url => {
  if (typeof url !== 'string' || url.includes('{{')) return semChaves(url);
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
};

// O rótulo automático do passo, quando ele não tem `description` (PLAN P16): o tipo e o que o
// distingue, como o n8n mostra embaixo do nome do nó.
function resumoDoPasso(passo) {
  const p = json(passo) || {};
  switch (p.type) {
    case 'http':
      return `HTTP · ${p.method || ''} ${caminhoDaUrl(p.url)}`.trim();
    case 'tool':
      return `tool · ${p.name || ''}`;
    case 'if':
      return `if · ${semChaves(p.when)}`;
    case 'switch':
      return `switch · ${semChaves(p.value)}`;
    case 'set':
      return `set · ${Object.keys(p.vars || {}).join(', ')}`;
    case 'validate':
      return `validate · ${p.rule || ''}`;
    case 'filter':
      return `filter · ${semChaves(p.where)}`;
    case 'sort':
      return `sort · ${(p.by || []).map(c => semChaves(c?.field)).join(', ')}`;
    case 'date':
      return `date · ${p.op || ''}`;
    case 'branches':
      return `branches · ${(p.branches || []).length}`;
    // FLOW-04: o laço diz sobre o que repete (`for_each`) ou até quantas voltas (`while`); o
    // `aggregate`, o que faz com a lista, na ordem em que faz, separado por vírgula (um "·" só
    // por linha, como nos outros tipos).
    case 'for_each':
      return `for_each · ${semChaves(p.items)}`;
    case 'while':
      return `while · max ${p.max ?? ''}`.trim();
    case 'aggregate': {
      const etapas = [
        p.flatten === true && 'flatten',
        p.unique_by && `unique ${p.unique_by}`,
        p.group_by && `group ${p.group_by}`,
        ...Object.keys(p.compute || {}),
      ].filter(Boolean);
      return `aggregate · ${etapas.length ? etapas.join(', ') : semChaves(p.items)}`;
    }
    // FLOW-05: o que o passo de arquivo lê ou entrega, e o nome do PDF.
    case 'attachments':
      return `attachments · ${[].concat(p.kinds || ['image', 'document']).join(', ')}`;
    case 'image.normalize':
    case 'artifact':
      return `${p.type} · ${semChaves(p.file)}`;
    case 'document.render':
      return `document.render · ${semChaves(p.name ?? '')}`.trim();
    case 'fail':
      return `fail · ${semChaves(p.message)}`;
    // FLOW-07: o tamanho do código vem à parte (`linhasDeCodigo`), no idioma da tela.
    case 'code':
      return 'JavaScript';
    default:
      return String(p.type || '');
  }
}

// Task 6.6: o POST/PUT/PATCH com `effect: prepare` cria algo de trabalho no outro sistema (o
// carrinho vazio), mas não é a gravação do caminho: o nó leva o selo "prepara", e não "escrita".
const METODOS_COM_EFFECT = ['POST', 'PUT', 'PATCH'];
const prepara = p =>
  p.type === 'http' &&
  p.effect === 'prepare' &&
  METODOS_COM_EFFECT.includes(String(p.method || 'GET').toUpperCase());

// Task 6.3: o POST/PUT/PATCH com `effect: read` só consulta (a simulação de frete da VTEX).
const soLe = p =>
  p.type === 'http' &&
  p.effect === 'read' &&
  METODOS_COM_EFFECT.includes(String(p.method || 'GET').toUpperCase());

const escrita = p =>
  p.type === 'http' &&
  !prepara(p) &&
  !soLe(p) &&
  !METODOS_DE_LEITURA.includes(String(p.method || 'GET').toUpperCase());

// A ferramenta que um passo `tool` chama, com a versão quando o passo a fixa: se ela grava, só o
// catálogo do agente sabe, e a tela guarda o que a sessão de teste disse sobre ela por este nome.
const ferramenta = p =>
  p.type === 'tool'
    ? [p.name, p.version].filter(v => v != null).join('@')
    : null;

const rotuloDaLista = (passo, chave) => {
  const valor = passo?.get?.(chave);
  return typeof valor === 'string' && ID_VALIDO.test(valor) ? valor : null;
};

const caminhoDaChave = chave =>
  CHAVE_SIMPLES.test(chave) ? `.${chave}` : `[${JSON.stringify(chave)}]`;

// O gatilho do flow como o nó de topo e a caixa dele o mostram. `linhas` são as do servidor (o
// `gatilhosDoEditor`), `previas` as frases das agendas, e `jaPublicado` diz se o nome tem versão
// publicada (o show do flow).
// - `tipo`: o do `gatilho:`, ou o do `gatilhos:` de antes; sem gatilho, o flow é de Agente. Só o que
//   nunca foi publicado e não é a ferramenta HTTP, sempre de Agente (A2), fica sem tipo: é o `vazio`,
//   o nó que pede o gatilho.
// - `id`: o do gatilho, que casa as linhas e as prévias (o tipo, no `gatilho:`, P2).
// - `ligado`: na agenda e no webhook com linha publicada, se ela está ligada; senão, nulo.
export function gatilhoDoFlow(
  texto,
  { linhas = [], previas = {}, jaPublicado = true } = {}
) {
  const { doc } = documentoLido(texto);
  const raiz = isMap(doc.contents) ? doc.contents : null;
  const gatilho = tipoDoGatilho({
    gatilho: json(raiz?.get('gatilho', true)),
    gatilhos: json(raiz?.get('gatilhos', true)),
  });
  const vazio = !gatilho && !jaPublicado && !formaDeFerramentaHttp(json(raiz));
  const publicada =
    gatilho &&
    linhas.find(
      linha =>
        linha.publicado &&
        linha.gatilho_id === gatilho.id &&
        linha.tipo === gatilho.tipo
    );
  return {
    tipo: gatilho?.tipo || (vazio ? null : 'agente'),
    id: gatilho?.id ?? null,
    config: gatilho?.config || {},
    frase: (gatilho && previas[gatilho.id]?.frase) || null,
    ligado: publicada ? !!publicada.ligado : null,
    vazio,
  };
}

// O nó e as conexões de um flow. `ponteiro` é o JSON pointer do passo no documento (`/steps/1`),
// que as edições usam; `id` é o caminho, que o rastro e os eventos usam. `gatilhosDoEditor` é o que
// o editor sabe do gatilho de fora do texto (`gatilhoDoFlow`).
export function grafoDoFlow(texto, gatilhosDoEditor = {}) {
  const { doc } = documentoLido(texto);
  const raiz = isMap(doc.contents) ? doc.contents : null;
  const params = raiz?.get('params', true);
  const nos = [
    {
      id: GATILHO,
      type: 'gatilho',
      data: {
        ponteiro: '',
        params: isMap(params)
          ? params.items.map(par => String(par.key?.value))
          : [],
        saidas: ['saida'],
        insercoes: { saida: { lista: '/steps', indice: 0 } },
        saidasLivres: [],
        ...gatilhoDoFlow(texto, gatilhosDoEditor),
      },
    },
  ];
  const arestas = [];
  const usados = new Set([GATILHO]);

  // O que apagar a conexão faz (`soltarTrecho`): `posicao` é onde ela chega (a lista e o índice do
  // passo, ou o laço, no fim da volta e na próxima volta). O `bloco` da recusa é o id do nó.
  const apagarDe = (saida, posicao) => {
    if (posicao.laco) return { recusa: 'laco', bloco: posicao.laco };
    if (saida.de === GATILHO) {
      return {
        tipo: 'lista',
        lista: '/steps',
        obrigatoria: true,
        gatilho: true,
      };
    }
    const direto = saida.alvoDireto;
    if (
      direto &&
      direto.lista === posicao.lista &&
      direto.indice === posicao.indice
    ) {
      if (posicao.indice > 0) {
        return { tipo: 'trecho', lista: posicao.lista, indice: posicao.indice };
      }
      return {
        tipo: 'lista',
        lista: posicao.lista,
        obrigatoria: !opcional(posicao.lista),
      };
    }
    return { recusa: 'juncao', bloco: saida.juncao || saida.bloco || saida.de };
  };

  const ligar = (saidas, alvo, entrada = 'entrada', posicao = {}) =>
    saidas.forEach(saida =>
      arestas.push({
        id: `${saida.de}>${saida.handle}>${alvo}`,
        source: saida.de,
        sourceHandle: saida.handle,
        target: alvo,
        targetHandle: entrada,
        type: 'conexao',
        data: {
          rotulo: saida.rotulo || null,
          literal: !!saida.literal,
          ordem: saida.ordem ?? null,
          quando: saida.quando ?? null,
          insercao: saida.insercao ?? null,
          erro: !!saida.erro,
          volta: !!saida.volta,
          laco: saida.laco ?? null,
          layoutDe: saida.layoutDe ?? null,
          juncao: saida.juncao ?? null,
          maisDaJuncao: !!saida.maisDaJuncao,
          apagar: apagarDe(saida, posicao),
        },
      })
    );

  // Desce numa lista de passos. `entradas` são as saídas pendentes que chegam ao primeiro passo; a
  // função devolve as que sobram no fim (para o que vem depois do bloco). `respondido` marca o que
  // roda depois de um `respond`, que o canvas põe na faixa "depois de responder"; `lacos`, os ids
  // dos laços em volta, de fora para dentro, que dizem em que contêiner o nó fica.
  // `contexto`: `desligado` (o passo está desativado, ou dentro de um desativado) e `corrente` (o
  // índice da corrente solta, ou nulo no flow).
  function descer(
    lista,
    ponteiroDaLista,
    caminhoDaLista,
    entradas,
    respondido,
    lacos = [],
    contexto = { desligado: false, corrente: null }
  ) {
    let pendentes = entradas;
    let depois = respondido;
    lista.items.forEach((passo, i) => {
      if (!isMap(passo)) return;
      const ponteiro = `${ponteiroDaLista}/${i}`;
      const rotulo = rotuloDaLista(passo, 'id') ?? i;
      let id = `${caminhoDaLista}[${rotulo}]`;
      if (usados.has(id)) id = ponteiro;
      usados.add(id);
      const p = json(passo);
      const depoisDeResponder = depois || pendentes.some(s => s.respondido);

      ligar(pendentes, id, 'entrada', { lista: ponteiroDaLista, indice: i });
      const proxima = { lista: ponteiroDaLista, indice: i + 1 };
      const desativadoProprio = p.disabled === true;
      const dentroDoContexto = {
        ...contexto,
        desligado: contexto.desligado || desativadoProprio,
      };
      // `alvoDireto`: o passo a que a saída leva sem passar por bloco nenhum (o seguinte na lista, ou o
      // primeiro da lista daquela saída). É o que diz se apagar a conexão solta um trecho.
      const saida = (handle, extra = {}) => ({
        de: id,
        handle,
        insercao: proxima,
        respondido: depoisDeResponder,
        alvoDireto: extra.insercao ?? proxima,
        ...extra,
      });
      // O ramo vazio sai do próprio bloco e cria a lista com o passo novo: esse lugar é fixo.
      const ramo = (no, sub, caminhoSub, entrada, dentro = lacos) =>
        isSeq(no) && no.items.length
          ? descer(
              no,
              sub,
              caminhoSub,
              [entrada],
              depoisDeResponder,
              dentro,
              dentroDoContexto
            )
          : [{ ...entrada, insercao: { lista: sub, indice: 0 }, fixa: true }];
      // A junção dos ramos deste bloco: o que sai do fim de um ramo e segue para depois dele insere
      // depois do bloco. Num bloco dentro de outro, o de fora a refaz: o "+" que chega ao passo
      // seguinte (ou ao fim da volta) insere logo antes dele, na lista dele.
      const juntar = finais => {
        let primeira = true;
        return finais.map(s => {
          if (s.fixa) return s;
          const junta = {
            ...s,
            insercao: proxima,
            juncao: id,
            maisDaJuncao: primeira,
          };
          primeira = false;
          return junta;
        });
      };
      const quandoFalha = passo.get('on_error', true);
      const rota = isMap(quandoFalha) ? quandoFalha.get('steps', true) : null;

      const no = {
        id,
        type: 'passo',
        data: {
          ponteiro,
          caminho: id,
          tipo: p.type,
          idDoPasso: typeof p.id === 'string' ? p.id : null,
          descricao: typeof p.description === 'string' ? p.description : null,
          nota: typeof p.note === 'string' ? p.note : null,
          resumo: resumoDoPasso(passo),
          linhasDeCodigo:
            p.type === 'code' && typeof p.code === 'string'
              ? p.code.split('\n').length
              : null,
          escrita: escrita(p),
          prepara: prepara(p),
          ferramenta: ferramenta(p),
          lista: ponteiroDaLista,
          indice: i,
          total: lista.items.length,
          saidas: [],
          depoisDeResponder,
          lacos,
          // Os selos do nó: `tenta N×` (retry) e `continua` (on_error: continue). O Router mostra
          // a política dele no selo próprio.
          retry: Number.isInteger(p.retry?.times) ? p.retry.times : null,
          continua: p.on_error === 'continue' && p.type !== 'branches',
          rotaDeErro: isSeq(rota),
          // FLOW-11 (S11): desativado no YAML (ele, ou o bloco em volta), e solto (`detached`).
          desativado: contexto.desligado || desativadoProprio,
          desativadoProprio,
          solto: contexto.corrente !== null,
          corrente: contexto.corrente,
          // Onde entra o passo ligado a cada saída (5.4): logo depois dele, ou o começo da lista.
          insercoes: { saida: proxima, depois: proxima },
          saidasLivres: [],
        },
      };
      nos.push(no);
      const entrarEm = (handle, sub) => {
        no.data.insercoes[handle] = { lista: sub, indice: 0 };
      };

      let novas = [];
      if (p.type === 'if') {
        no.data.saidas = ['verdadeiro', 'falso'];
        entrarEm('verdadeiro', `${ponteiro}/then`);
        entrarEm('falso', `${ponteiro}/else`);
        const entao = ramo(
          passo.get('then', true),
          `${ponteiro}/then`,
          `${id}.then`,
          saida('verdadeiro', {
            rotulo: 'verdadeiro',
            insercao: { lista: `${ponteiro}/then`, indice: 0 },
          })
        );
        const senao = isSeq(passo.get('else', true))
          ? ramo(
              passo.get('else', true),
              `${ponteiro}/else`,
              `${id}.else`,
              saida('falso', {
                rotulo: 'falso',
                insercao: { lista: `${ponteiro}/else`, indice: 0 },
              })
            )
          : [
              saida('falso', {
                rotulo: 'falso',
                insercao: { lista: `${ponteiro}/else`, indice: 0 },
                fixa: true,
              }),
            ];
        novas = juntar([...entao, ...senao]);
      } else if (p.type === 'switch') {
        const casos = passo.get('cases', true);
        const chaves = isMap(casos)
          ? casos.items.map(par => String(par.key?.value))
          : [];
        no.data.saidas = [...chaves.map((_, k) => `caso-${k}`), 'padrao'];
        entrarEm('padrao', `${ponteiro}/default`);
        chaves.forEach((chave, k) => {
          const sub = `${ponteiro}/cases/${escapar(chave)}`;
          entrarEm(`caso-${k}`, sub);
          novas.push(
            ...ramo(
              casos.items[k].value,
              sub,
              `${id}.cases${caminhoDaChave(chave)}`,
              saida(`caso-${k}`, {
                rotulo: chave,
                literal: true,
                insercao: { lista: sub, indice: 0 },
              })
            )
          );
        });
        const padrao = passo.get('default', true);
        const sub = `${ponteiro}/default`;
        novas.push(
          ...(isSeq(padrao)
            ? ramo(
                padrao,
                sub,
                `${id}.default`,
                saida('padrao', {
                  rotulo: 'padrao',
                  insercao: { lista: sub, indice: 0 },
                })
              )
            : [
                saida('padrao', {
                  rotulo: 'padrao',
                  insercao: { lista: sub, indice: 0 },
                  fixa: true,
                }),
              ])
        );
        novas = juntar(novas);
      } else if (p.type === 'validate') {
        no.data.saidas = ['saida'];
        novas = [saida('saida')];
        entrarEm('invalido', `${ponteiro}/else`);
        const senao = passo.get('else', true);
        if (isSeq(senao)) {
          no.data.saidas = ['saida', 'invalido'];
          novas.push(
            ...ramo(
              senao,
              `${ponteiro}/else`,
              `${id}.else`,
              saida('invalido', {
                rotulo: 'invalido',
                insercao: { lista: `${ponteiro}/else`, indice: 0 },
              })
            )
          );
        }
      } else if (p.type === 'branches') {
        const ramos = passo.get('branches', true);
        const itens = isSeq(ramos) ? ramos.items : [];
        no.data.modo = p.mode || 'sequential';
        // Com rota de erro, a política do bloco é a rota, que a saída "se falhar" já mostra.
        no.data.politica = rota ? null : 'stop';
        if (typeof p.on_error === 'string') no.data.politica = p.on_error;
        no.data.match = p.match || 'all';
        no.data.ramos = itens.map(r => ({
          nome: isMap(r) ? String(r.get('name') ?? '') : '',
          quando:
            isMap(r) && r.get('when') !== undefined
              ? semChaves(r.get('when'))
              : null,
          politica: (isMap(r) && r.get('on_error')) || null,
        }));
        no.data.saidas = itens.map((_, k) => `ramo-${k}`);
        itens.forEach((r, k) => {
          const sub = `${ponteiro}/branches/${k}/steps`;
          entrarEm(`ramo-${k}`, sub);
          const nome = rotuloDaLista(r, 'name') ?? k;
          novas.push(
            ...ramo(
              isMap(r) ? r.get('steps', true) : null,
              sub,
              `${id}.branches[${nome}].steps`,
              saida(`ramo-${k}`, {
                rotulo: no.data.ramos[k].nome,
                literal: true,
                ordem: k + 1,
                quando: no.data.ramos[k].quando,
                insercao: { lista: sub, indice: 0 },
              })
            )
          );
        });
        novas = juntar(novas);
      } else if (LACOS.includes(p.type)) {
        const corpo = `${ponteiro}/steps`;
        const fim = `${id}#fim`;
        const dentro = [...lacos, id];
        no.data.saidas = ['corpo'];
        entrarEm('corpo', corpo);
        no.data.laco = {
          nome: typeof p.as === 'string' ? p.as : null,
          max: Number.isInteger(p.max) ? p.max : null,
          modo: p.mode === 'parallel' ? 'parallel' : 'sequential',
          pula: p.on_item_error === 'skip',
        };
        const finais = ramo(
          passo.get('steps', true),
          corpo,
          `${id}.steps`,
          saida('corpo', { insercao: { lista: corpo, indice: 0 } }),
          dentro
        );
        nos.push({
          id: fim,
          type: FIM_DA_VOLTA,
          data: { laco: id, lacos: dentro, depoisDeResponder },
        });
        ligar(finais, fim, 'entrada', { laco: id });
        ligar(
          [{ de: fim, handle: 'volta', volta: true, laco: id }],
          id,
          'volta',
          { laco: id }
        );
        novas = [
          saida('depois', {
            rotulo: 'depois_do_laco',
            laco: id,
            layoutDe: fim,
            fixa: true,
          }),
        ];
      } else if (!TERMINAIS.includes(p.type)) {
        no.data.saidas = ['saida'];
        novas = [saida('saida')];
        if (p.type === 'respond') depois = true;
      }
      entrarEm('erro', `${ponteiro}/on_error/steps`);
      if (isSeq(rota)) {
        const sub = `${ponteiro}/on_error/steps`;
        // Com duas saídas, a de sempre ganha o rótulo "ok", como no mockup aprovado.
        novas.forEach(s => {
          if (s.de === id && s.handle === 'saida' && !s.rotulo) s.rotulo = 'ok';
        });
        no.data.saidas = [...no.data.saidas, 'erro'];
        novas = [
          ...novas,
          ...ramo(
            rota,
            sub,
            `${id}.on_error.steps`,
            saida('erro', {
              rotulo: 'se_falhar',
              erro: true,
              insercao: { lista: sub, indice: 0 },
            })
          ),
        ];
      }
      // O que sai de dentro do bloco (o fim de um ramo, da rota de erro) segue dizendo de que bloco é:
      // a conexão dele ao passo seguinte é a junção, que existe enquanto o bloco existir.
      pendentes = novas.map(s =>
        s.de !== id && !s.bloco ? { ...s, bloco: id } : s
      );
    });
    return pendentes;
  }

  const passos = raiz?.get('steps', true);
  if (isSeq(passos)) {
    descer(
      passos,
      '/steps',
      'steps',
      [
        {
          de: GATILHO,
          handle: 'saida',
          insercao: { lista: '/steps', indice: 0 },
        },
      ],
      false
    );
  }
  // FLOW-11 (S11): cada corrente solta é um grupo sem entrada.
  const soltos = raiz?.get('detached', true);
  if (isSeq(soltos)) {
    soltos.items.forEach((corrente, k) => {
      const lista = isMap(corrente) && corrente.get('steps', true);
      if (isSeq(lista)) {
        descer(
          lista,
          `/detached/${k}/steps`,
          `detached[${k}].steps`,
          [],
          false,
          [],
          {
            desligado: false,
            corrente: k,
          }
        );
      }
    });
  }
  // As saídas sem conexão (o "+" na ponta, como o n8n).
  const ligadas = new Set(arestas.map(a => `${a.source}>${a.sourceHandle}`));
  nos.forEach(no => {
    if (no.type !== 'passo' && no.type !== 'gatilho') return;
    const portas = [
      ...(no.data.saidas || []),
      ...(no.data.laco ? ['depois'] : []),
    ];
    no.data.saidasLivres = portas.filter(
      porta => !ligadas.has(`${no.id}>${porta}`)
    );
  });
  return { nos, arestas };
}

// Posições de cima para baixo. `manuais` são os nós que a pessoa arrastou (pelo id): ficam onde
// ela os deixou até "Organizar".
//
// O dagre decide de que lado cada ramo fica; as portas de saída seguem a mesma ordem, da esquerda
// para a direita, para as conexões não se cruzarem embaixo do nó. O selo do Router continua sendo
// o da ordem no YAML (o id da porta), e não o da posição.
export function organizar({ nos, arestas }, manuais = {}) {
  const g = new graphlib.Graph();
  g.setGraph({
    rankdir: 'TB',
    nodesep: 56,
    // 72, e não 96: com o painel de teste aberto, o flow cabia em 2,5 nós (impeccable, 10/10). O
    // "+" e o selo de itens da conexão cabem no vão.
    ranksep: 72,
    marginx: 24,
    marginy: 24,
  });
  g.setDefaultEdgeLabel(() => ({}));
  nos.forEach(no => g.setNode(no.id, dimensoes(no)));
  // A seta da próxima volta não entra: com ela o dagre veria um ciclo e viraria o corpo de ponta
  // cabeça. O "depois do laço" entra a partir do fim da volta, para cair abaixo do contêiner.
  const doLayout = arestas.filter(a => !a.data?.volta);
  doLayout.forEach(a => g.setEdge(a.data?.layoutDe || a.source, a.target));
  layout(g);
  const xDoAlvo = (no, saida) =>
    Math.min(
      ...doLayout
        .filter(a => a.source === no.id && a.sourceHandle === saida)
        .map(a => g.node(a.target).x)
    );
  return nos.map(no => {
    const { x, y } = g.node(no.id);
    const { width, height } = dimensoes(no);
    const saidas =
      no.data.saidas?.length > 1 &&
      [...no.data.saidas].sort((a, b) => xDoAlvo(no, a) - xDoAlvo(no, b));
    return {
      ...no,
      data: saidas ? { ...no.data, saidas } : no.data,
      position: manuais[no.id] || {
        x: x - width / 2,
        y: y - height / 2,
      },
    };
  });
}

// FLOW-04 (task 4.1, mockup aprovado no checkpoint 4.0) — o contêiner do corpo de cada laço: o
// retângulo tracejado atrás dos passos de dentro dele e do fim da volta, como a faixa "depois de
// responder". O de um laço dentro de outro vai antes no cálculo, e o de fora o abraça com folga.
// Volta os nós do Vue Flow, o de fora antes do de dentro, com o retângulo em `data.retangulo`,
// que as conexões do laço usam para correr por fora dele.
const MARGEM_DO_CORPO = 28;
// O alto do contêiner leva o cabeçalho e, abaixo dele, a seta da próxima volta, que entra ali.
const CABECALHO_DO_CORPO = 30;
const FOLGA_ENTRE_CORPOS = 16;
export function corposDosLacos(posicionados) {
  const lacos = posicionados.filter(no => no.type === 'passo' && no.data.laco);
  const retangulos = {};
  [...lacos]
    .sort((a, b) => b.data.lacos.length - a.data.lacos.length)
    .forEach(laco => {
      const caixas = posicionados
        .filter(no => no.data?.lacos?.includes(laco.id))
        .map(no => {
          const { width, height } = dimensoes(no);
          return {
            x: no.position.x - MARGEM_DO_CORPO,
            y: no.position.y - MARGEM_DO_CORPO - CABECALHO_DO_CORPO,
            direita: no.position.x + width + MARGEM_DO_CORPO,
            baixo: no.position.y + height + MARGEM_DO_CORPO,
          };
        });
      lacos
        .filter(outro => outro.data.lacos.includes(laco.id))
        .forEach(outro => {
          const r = retangulos[outro.id];
          caixas.push({
            x: r.x - FOLGA_ENTRE_CORPOS,
            y: r.y - FOLGA_ENTRE_CORPOS - CABECALHO_DO_CORPO,
            direita: r.direita + FOLGA_ENTRE_CORPOS,
            baixo: r.baixo + FOLGA_ENTRE_CORPOS,
          });
        });
      if (!caixas.length) return;
      retangulos[laco.id] = {
        x: Math.min(...caixas.map(c => c.x)),
        y: Math.min(...caixas.map(c => c.y)),
        direita: Math.max(...caixas.map(c => c.direita)),
        baixo: Math.max(...caixas.map(c => c.baixo)),
      };
    });
  return lacos
    .filter(laco => retangulos[laco.id])
    .sort((a, b) => a.data.lacos.length - b.data.lacos.length)
    .map(laco => {
      const r = retangulos[laco.id];
      // A conexão do laço para o corpo entra pelo alto, na coluna do quadrado do laço; o cabeçalho
      // fica do outro lado, para o "+" dela não cair em cima do texto.
      const entrada = laco.position.x + 36 - r.x;
      const cabecalho =
        entrada < (r.direita - r.x) / 2 ? 'direita' : 'esquerda';
      return {
        id: `corpo-${laco.id}`,
        type: 'corpo',
        position: { x: r.x, y: r.y },
        width: r.direita - r.x,
        height: r.baixo - r.y,
        selectable: false,
        draggable: false,
        focusable: false,
        zIndex: -1,
        data: {
          laco: laco.id,
          cabecalho,
          tipo: laco.data.tipo,
          ...laco.data.laco,
          // `entrada`: onde a seta da próxima volta chega, na coluna do quadrado do laço (a da
          // conexão que desce para o primeiro passo do corpo), entre o cabeçalho e o primeiro passo.
          retangulo: {
            ...r,
            entrada: {
              x: laco.position.x + 36,
              y: r.y + CABECALHO_DO_CORPO + 6,
            },
          },
        },
      };
    });
}

// A faixa "depois de responder" (D16): o retângulo que cobre os passos que rodam depois do
// `respond`, atrás deles.
export function faixaDepoisDeResponder(posicionados) {
  const depois = posicionados.filter(no => no.data.depoisDeResponder);
  if (!depois.length) return null;
  const margem = 28;
  const x = Math.min(...depois.map(no => no.position.x)) - margem;
  const y = Math.min(...depois.map(no => no.position.y)) - margem - 12;
  const direita =
    Math.max(...depois.map(no => no.position.x + LARGURA_DO_NO)) + margem;
  const baixo =
    Math.max(...depois.map(no => no.position.y + ALTURA_DO_NO)) + margem;
  return {
    id: 'faixa-depois-de-responder',
    type: 'faixa',
    position: { x, y },
    width: direita - x,
    height: baixo - y,
    selectable: false,
    draggable: false,
    focusable: false,
    zIndex: -1,
    data: {},
  };
}

// FLOW-11 (task 5.4, S11): o contorno tracejado de cada corrente solta, atrás dos passos dela, com
// o rótulo "solta · não roda" no alto. Como a faixa "depois de responder", não pega clique.
const MARGEM_DA_CORRENTE = 24;
const CABECALHO_DA_CORRENTE = 26;
export function correntesSoltas(posicionados) {
  const porCorrente = {};
  posicionados
    .filter(no => no.type === 'passo' && no.data.solto)
    .forEach(no => {
      (porCorrente[no.data.corrente] ||= []).push(no);
    });
  return Object.entries(porCorrente).map(([corrente, passos]) => {
    const caixas = passos.map(no => ({
      x: no.position.x,
      y: no.position.y,
      direita: no.position.x + LARGURA_DO_NO,
      baixo: no.position.y + dimensoes(no).height,
    }));
    const x = Math.min(...caixas.map(c => c.x)) - MARGEM_DA_CORRENTE;
    const y =
      Math.min(...caixas.map(c => c.y)) -
      MARGEM_DA_CORRENTE -
      CABECALHO_DA_CORRENTE;
    const direita =
      Math.max(...caixas.map(c => c.direita)) + MARGEM_DA_CORRENTE;
    const baixo = Math.max(...caixas.map(c => c.baixo)) + MARGEM_DA_CORRENTE;
    return {
      id: `corrente-${corrente}`,
      type: 'solto',
      position: { x, y },
      width: direita - x,
      height: baixo - y,
      selectable: false,
      draggable: false,
      focusable: false,
      zIndex: -1,
      data: { corrente: Number(corrente), passos: passos.length },
    };
  });
}

// Os problemas do compilador no passo mais fundo cujo ponteiro os contém (`/steps/4/value/x` cai
// em `/steps/4`); o que é do topo do flow (`/params`, `/efeito`) vai para o gatilho.
export function problemasPorNo(nos, problemas) {
  const porNo = {};
  const passos = nos
    .filter(no => no.type === 'passo')
    .sort((a, b) => b.data.ponteiro.length - a.data.ponteiro.length);
  (problemas || []).forEach(problema => {
    const ponteiro = problema.ponteiro || '';
    const dono =
      passos.find(
        no =>
          ponteiro === no.data.ponteiro ||
          ponteiro.startsWith(`${no.data.ponteiro}/`)
      )?.id ||
      // FLOW-11: o aviso da corrente solta (`/detached/0`) vai ao primeiro passo dela.
      passos.find(no => no.data.ponteiro === `${ponteiro}/steps/0`)?.id ||
      (ponteiro.startsWith('/steps') || ponteiro.startsWith('/detached')
        ? null
        : GATILHO);
    if (dono) (porNo[dono] ||= []).push(problema);
  });
  return porNo;
}

// Quantos passos moram dentro do passo no ponteiro, em qualquer nível (os ramos, o corpo do laço,
// a rota de erro): é o que excluir leva junto, e o que o canvas pergunta antes (correção de 14/10).
export const passosDentro = (nos, ponteiro) =>
  nos.filter(
    no => no.type === 'passo' && no.data.ponteiro.startsWith(`${ponteiro}/`)
  ).length;

// Onde entra o passo escolhido na biblioteca sem um "+": depois do passo selecionado na lista dele
// (antes, quando ele encerra o flow); sem seleção, antes do `return`/`fail` que fecha o flow.
export function insercaoPadrao(nos, selecionado) {
  const no = nos.find(
    n => n.type === 'passo' && n.data.ponteiro === selecionado
  );
  if (no) {
    const { lista, indice, tipo } = no.data;
    return { lista, indice: TERMINAIS.includes(tipo) ? indice : indice + 1 };
  }
  const principais = nos.filter(
    n => n.type === 'passo' && n.data.lista === '/steps'
  );
  const fim = principais[principais.length - 1];
  if (!fim) return { lista: '/steps', indice: 0 };
  return {
    lista: '/steps',
    indice: TERMINAIS.includes(fim.data.tipo)
      ? fim.data.indice
      : fim.data.indice + 1,
  };
}

// A vista ao abrir o canvas, como a do n8n: zoom de 100%. O flow que cabe na área fica no centro
// dela; o que não cabe abre pelo começo, o gatilho centrado no alto, e o resto se alcança
// arrastando. Encaixar abaixo de 100% ao abrir foi medido ilegível na revisão de UX de 08/10 (a
// legenda do nó, de 11 px, caía para 8 px a 75%). `limites` e `inicio` são retângulos do flow
// (`getRectOfNodes`); `area`, o tamanho do canvas na tela. O botão "Encaixar" continua encaixando
// tudo, sem esse piso.
const MARGEM_AO_ABRIR = 40;
const ZOOM_MINIMO_AO_ABRIR = 1;
export function vistaInicial(limites, inicio, area) {
  const encaixe = Math.min(
    (area.width - 2 * MARGEM_AO_ABRIR) / limites.width,
    (area.height - 2 * MARGEM_AO_ABRIR) / limites.height,
    1
  );
  const zoom = Math.max(encaixe, ZOOM_MINIMO_AO_ABRIR);
  const centro = encaixe >= ZOOM_MINIMO_AO_ABRIR ? limites : inicio;
  return {
    zoom,
    x: area.width / 2 - (centro.x + centro.width / 2) * zoom,
    y:
      centro === limites
        ? area.height / 2 - (limites.y + limites.height / 2) * zoom
        : MARGEM_AO_ABRIR - inicio.y * zoom,
  };
}
